import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Pagination,
  useTheme,
  styled,
  useMediaQuery
} from '@mui/material';
import { useAssetsContext } from '../../context/AssetsContext';

const FooterContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 8,
  left: 8,
  right: 8,
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  zIndex: 10,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderRadius: 5,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 0 10px rgba(0,0,0,0.6)'
    : '0 0 6px rgba(0,0,0,0.1)',
  // 👇 Let the container's height adjust and stack elements for mobile
  flexDirection: 'row', // Default direction
  height: '40px', // Default height
  transition: 'height 0.3s ease-in-out',

  // Use a media query to apply mobile-specific styles
  [theme.breakpoints.down('sm')]: {
    height: 'auto', // Allow height to grow
    flexDirection: 'column', // Stack items vertically
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    gap: theme.spacing(1.5) // Add space between stacked items
  }
}));

const PaginationFooter: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    totalAssets,
    page,
    setPage,
    resultsPerPage,
    setResultsPerPage,
  } = useAssetsContext();

  const totalPages = Math.ceil(totalAssets / resultsPerPage);
  const startItem = totalAssets > 0 ? (page - 1) * resultsPerPage + 1 : 0;
  const endItem = Math.min(page * resultsPerPage, totalAssets);

  return (
    <FooterContainer>
      {/* Rows Per Page */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        order: isMobile ? 3 : 1 // On mobile, flex order 3 (last item)
      }}>
        <Typography sx={{ color: theme.palette.text.primary, mr: 1 }}>
          Rows:
        </Typography>
        <Select
          value={resultsPerPage}
          onChange={(e) => setResultsPerPage(Number(e.target.value))}
          size="small"
          sx={{
            color: theme.palette.text.primary,
            height: '30px',
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
          }}
        >
          {[10, 25, 50, 100].map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Total Assets */}
      <Typography sx={{ color: theme.palette.text.primary, fontWeight: 500, order: 2 }}>
        {startItem}-{endItem} of {totalAssets.toLocaleString()} Assets
      </Typography>

      {/* Pagination */}
      <Pagination
        count={totalPages}
        page={page}
        onChange={(_, value) => setPage(value)}
        color="primary"
        // 👇 Key changes for mobile responsiveness
        size={isMobile ? 'small' : 'medium'}
        siblingCount={isMobile ? 1 : 2} // Shows fewer page numbers on mobile
        sx={{
          order: isMobile ? 1 : 3, // On mobile, flex order 1 (first item)
          '& .MuiPaginationItem-root.Mui-selected': {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.getContrastText(theme.palette.error.main),
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
          },
        }}
      />
    </FooterContainer>
  );
};

export default PaginationFooter;
